import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import { SuggestionSelect, GetValuesResult, ISuggestionValue } from '../../../_common/platform/tsx/SuggestionSelect';

export { GetValuesResult, ISuggestionValue } from '../../../_common/platform/tsx/SuggestionSelect';

import SuggestionListField from '../../../_common/platform/tsx/SuggestionListField';
import { ProgressBar } from '../../../_common/platform/tsx/ProgressBar';

import ParameterFieldBase, {
    IParameterFieldBaseOptions,
    IValidator
} from '../../../_common/platform/ts/ParameterFieldBase';

import { IWidth } from '../../../_common/platform/ts/Interfaces';
import { IGetEntityValuesParameters, useGetEntityValuesHook } from './selectors/useGetEntityValuesHook';

export type SelectionTypes = 'multiply' | 'single' | string;
export type ControlTypes = 'dropdown' | 'list' | string;

export const DEFAULT_NUMBER_OF_LIST_ROWS = 10;

export interface IEntityValuesSelectOptions extends IParameterFieldBaseOptions {
    rows?: number;
}

export interface IEntityValuesSelectProperties extends IGetEntityValuesParameters {
    value?: ISuggestionValue[];
    selectionType?: SelectionTypes;
    controlType?: ControlTypes;
    displayValues?: ISuggestionValue[];
    keySelect?: boolean;
    allowNull?: boolean;
    placeholder?: string;
    tooltip?: string;
    usePlaceholderAsTitle?: boolean;
    width?: IWidth;
    minWidth?: IWidth;
    tabIndex?: number;
    readOnly?: boolean;
    hideSelectAllAndClear?: boolean;
    hideSearchField?: boolean;
    selectFirstItemByDefault?: boolean;
    checkValueExistsInValueList?: boolean;
    setFocus?: boolean;
    options?: IEntityValuesSelectOptions;
    name?: string;
    glob: IGlob;

    getValues?(): GetValuesResult;
    getDisplayValue?(value: string): string;
    onChange?(value: ISuggestionValue[]): void;
}

export default function EntityValuesSelect(props: IEntityValuesSelectProperties) {
    const modelsAreLoaded = props.glob.EntityModelsStore.useLoadModelsHook([props.dataSourceID]);
    const getValues = useGetEntityValuesHook(props);

    const theme = props.theme ?? props.options?.theme;

    const getListView = (value: ISuggestionValue[], singleSelectMode: boolean, allowNull: boolean) => {
        const listThreshold = props.options?.rows ?? DEFAULT_NUMBER_OF_LIST_ROWS;
        const showingType = props.globalFilter != null ? props.globalFilter.showingType : 'all';

        let getValuesFunction: () => GetValuesResult;

        if (showingType !== 'all') {
            // Refresh it each time if Valid+Invalid functionality is on. Checking if refresh is needed in the case is too complex.
            // This code creates new function each time making SuggestionListField run reload.
            getValuesFunction = () => {
                if (props.getValues != null) {
                    return props.getValues();
                } else {
                    return getValues();
                }
            };
        } else {
            getValuesFunction = props.getValues ?? getValues;
        }

        return (
            <SuggestionListField
                value={value}
                displayValues={props.displayValues}
                allowNull={allowNull}
                keySelect={props.keySelect}
                singleSelectMode={singleSelectMode}
                listThreshold={listThreshold}
                readOnly={props.readOnly}
                hideSelectAllAndClear={props.hideSelectAllAndClear}
                hideSearchField={props.hideSearchField}
                theme={theme}
                getValues={getValuesFunction}
                getDisplayValue={props.getDisplayValue}
                width={props.width}
                minWidth={props.minWidth}
                onChange={props.onChange}
            />
        );
    };

    const isListView = props.controlType === 'list';

    let content: React.JSX.Element;
    let inProgress = !modelsAreLoaded;

    if (!inProgress && isListView && props.getValues == null && props.globalFilter == null) {
        inProgress = props.glob.EntityModelsStore.entityValuesAreLoading(
            props.dataSourceID,
            props.attributeData,
            props.localFilter,
            props.sortingType
        );
    }

    if (inProgress) {
        content = <ProgressBar />;
    } else {
        const singleSelectMode = props.selectionType !== 'multiply';
        const { options } = props;
        // By default allowNull === true, but it can be undefined. So 'undefined' means 'true'.
        let allowNull = props.allowNull;

        if (options != null) {
            if (allowNull == null) {
                allowNull = options.allowNull;
            }
        }

        if (isListView) {
            content = getListView(props.value, singleSelectMode, allowNull);
        } else {
            content = (
                <SuggestionSelect
                    value={props.value}
                    displayValues={props.displayValues}
                    keySelect={props.keySelect}
                    singleSelectMode={singleSelectMode}
                    allowNull={allowNull}
                    placeholder={props.placeholder}
                    tooltip={props.tooltip}
                    usePlaceholderAsTitle={props.usePlaceholderAsTitle}
                    width={props.width}
                    minWidth={props.minWidth}
                    readOnly={props.readOnly}
                    hideSelectAllAndClear={props.hideSelectAllAndClear}
                    hideSearchField={props.hideSearchField}
                    selectFirstItemByDefault={props.selectFirstItemByDefault}
                    checkValueExistsInValueList={props.checkValueExistsInValueList}
                    tabIndex={props.tabIndex}
                    setFocus={props.setFocus}
                    options={props.options}
                    theme={theme}
                    name={props.name}
                    glob={props.glob}
                    getValues={props.getValues || getValues}
                    getDisplayValue={props.getDisplayValue}
                    onChange={props.onChange}
                />
            );
        }
    }

    return <div className="entity-values-select">{content}</div>;
}

export class EntityValuesSelectField extends ParameterFieldBase<IParameterFieldBaseOptions> {
    public static EditComponent = EntityValuesSelect;
    public getDefaultDirectOptions() {
        return {};
    }

    constructor(
        glob: IGlob,
        title: string,
        validators?: IValidator[],
        defaultVal?: any,
        options?: IParameterFieldBaseOptions,
        directOptions?: IEntityValuesSelectProperties
    ) {
        super(glob, title, [], validators, defaultVal, options, directOptions);
    }
}
